/** @module @ignore */
import * as React from 'react';
import { Layer, MapMouseEvent, Source, useMap } from 'react-map-gl/mapbox';
import { Feature, FeatureCollection, Polygon } from 'geojson';

import { IStructure } from '../types/IStructure';
import { PolygonUtils } from '../util/PolygonUtils';
import { useGlobalState } from '../contexts/state/GlobalStateContext';
import { polygon } from '@turf/helpers';
import bbox from '@turf/bbox';
import booleanIntersects from '@turf/boolean-intersects';
import { BBox } from 'geojson';

// Check if two bounding boxes overlap
const bboxOverlaps = (a: BBox, b: BBox): boolean => {
  // bbox format: [minX, minY, maxX, maxY]
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
};

const StructuresLayer = () => {
  const globalState = useGlobalState();
  const map = useMap().current!;

  const structureToPolygon = (structure: IStructure): Feature<Polygon> => {
    if(structure.type == 'polygon') {
      return polygon([[...structure.points.map(p => [ p.lng, p.lat]), [structure.points[0].lng, structure.points[0].lat]]]);
    } else { // circle
      return PolygonUtils.getCirclePolygon(structure.point, structure.radius);
    }
  }

  // Calculate polygons and intersections only when structures change
  // Uses bounding box pre-filter to avoid expensive booleanIntersects calls
  const structureData = React.useMemo(() => {
    const polys = globalState.structures.map(s => structureToPolygon(s));
    const boxes = polys.map(p => bbox(p));
    const intersects = globalState.structures.map((_, idx) =>
      globalState.structures.some((_, n) => {
        if (idx === n) return false;
        // Quick bbox check first - if boxes don't overlap, polygons can't intersect
        if (!bboxOverlaps(boxes[idx], boxes[n])) return false;
        // Boxes overlap - do the expensive intersection check
        return booleanIntersects(polys[idx], polys[n]);
      })
    );
    return { polys, intersects };
  }, [globalState.structures]);

  // Build GeoJSON - recalculates on selection/mode change but uses cached intersection data
  const getJSON = React.useMemo((): FeatureCollection => {
    const { polys, intersects } = structureData;
    const showAllStructures = globalState.mode === 'box-select' || globalState.mode === 'areas' || globalState.mode === 'area-editor';

    return {
      type: 'FeatureCollection',
      features: globalState.structures.map((structure, idx) => {
        const isSelected = globalState.selectedStructures.includes(structure);
        const hideSelected = isSelected && !showAllStructures;

        return {
          id: idx,
          type: 'Feature',
          properties: { intersects: intersects[idx] },
          geometry: {
            type: "Polygon",
            coordinates: hideSelected ? [] : polys[idx].geometry.coordinates
          }
        }
      })
    }
  }, [structureData, globalState.structures, globalState.selectedStructures, globalState.mode]);

  const handleClick = (e: MapMouseEvent) => {
    if(globalState.mode === 'area-builder') return;
    if(!e.features) return;
    if(e.features.length == 0) return;
    const feature = e.features[0];
    if(feature.layer?.id != 'structures') return;
    const structure = globalState.structures[feature.id as number];
    const additive = e.originalEvent.ctrlKey || e.originalEvent.metaKey;
    // Use a timeout to ensure polygon selection comes after deselection:
    setTimeout(() => {
      globalState.toggleStructureSelection(structure, additive);
      globalState.setMode(null);
    }, 0);
  }

  React.useEffect(() => {
    map.on("click", "structures", handleClick);
    return () => {
      map.off("click", "structures", handleClick);
    }
  }, [globalState.structures, globalState.selectedStructures, globalState.mode]);

  return (
    <Source generateId type="geojson" data={getJSON}>
      <Layer 
        id="structures" 
        type="fill"
        paint={{
          'fill-color': [
            'case',
            ['get', 'intersects'],
            'red',
            'lightblue'
            ],      
          'fill-opacity': 0.5
        }}
        />
      <Layer 
        type="line"
        paint={{
          'line-color': 'black',
          'line-width': 1
        }}
        />
  </Source>    
  );
}

export { StructuresLayer }
